import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ibanValidator, normalizeIban } from '../../../_helpers/validators/iban.validator';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../../services/clinical-onboarding.service';
import { ToastService } from '../../../services/general/toast.service';
import {
  CLINICIAN_MACRO_STATUS_META,
  ClinicianCandidateDocument,
  ClinicianMacroStatus,
  EmployerType,
  FiscalRegime,
} from '../../../models/clinical-onboarding.model';

/**
 * DBEK-405 (ridisegno "candidato"): wizard self-service del clinico per completare il
 * pacchetto contrattuale (anagrafica/datore di lavoro/IBAN/regime fiscale, documenti)
 * prima di poter rispondere al survey - piano §4. Raggiunta dal link d'invito
 * (InvitationController::acceptSurveyInvitation/acceptCandidateInvitation), route
 * protetta da AuthGuard. Rotta DECOUPLATA da survey_id (candidate_id, non survey_id) -
 * candidate_id resta stabile per tutto il ciclo di vita, anche quando la survey a cui
 * e' associato churna id ad ogni salvataggio bozza (vedi Candidate::currentSurvey()).
 *
 * Ridisegno "form unico" (feedback utente: "non deve essere a stepper ma [...] un
 * unico grande form cosi' permette al clinico di prendere visione ai singoli file e
 * restituire in maniera piu' flessibile"): niente piu' <mat-stepper> - tutte le
 * sezioni vivono nella stessa pagina, il clinico puo' aprire/restituire i documenti
 * in qualunque ordine invece di essere guidato passo-passo.
 */
@Component({
  selector: 'app-clinical-onboarding-wizard-page',
  templateUrl: './clinical-onboarding-wizard-page.component.html',
  styleUrls: ['./clinical-onboarding-wizard-page.component.scss'],
  standalone: false,
})
export class ClinicalOnboardingWizardPageComponent implements OnInit {
  candidateId: string = null;
  loading = true;
  saving = false;
  saveAttempted = false;
  documents: ClinicianCandidateDocument[] = [];
  uploadingDocumentId: number = null;
  macroStatus: string = null;
  paAuthorizationStatus: string = null;
  manualOverride = false;
  canEditBankFiscalData = true;
  // Risolti ad ogni load() dal backend (mai memorizzati/derivati lato client): l'id
  // survey REALE corrente puo' cambiare da un salvataggio bozza nel frattempo, e
  // "imported" indica se esiste gia' un vero team member per poter rispondere.
  surveyId: number = null;
  surveyIsDraft = true;
  imported = false;

  readonly EmployerType = EmployerType;
  readonly FiscalRegime = FiscalRegime;
  readonly ClinicianMacroStatus = ClinicianMacroStatus;
  readonly macroStatusMeta = CLINICIAN_MACRO_STATUS_META;

  form: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clinicalOnboardingService: ClinicalOnboardingService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    this.form = this.fb.group({
      title: [''],
      employer_type: ['', Validators.required],
      employer_name: [''],
      iban: ['', [Validators.required, ibanValidator()]],
      tax_code: ['', Validators.required],
      fiscal_regime: ['', Validators.required],
    });

    // employer_name e' obbligatorio solo quando employer_type=PUBLIC (nel template
    // compare condizionalmente solo in quel caso) - tenuto sincronizzato ad ogni
    // cambio di employer_type, non solo al submit.
    this.form.get('employer_type').valueChanges.subscribe((value) => {
      this.updateEmployerNameValidator(value);
    });
  }

  fieldError(field: string): string {
    const errors = this.form.get(field)?.errors;
    if (!errors) {
      return '';
    }
    if (errors['server']) {
      return errors['server'];
    }

    const prefix = 'components.clinical-onboarding.wizard.';
    if (errors['required']) {
      return this.translateService.instant(prefix + (field === 'iban' ? 'error-iban-required' : 'error-required'));
    }
    if (errors['ibanChars']) {
      return this.translateService.instant(prefix + 'error-iban-chars');
    }
    if (errors['ibanStart']) {
      return this.translateService.instant(prefix + 'error-iban-start');
    }
    if (errors['ibanSize']) {
      return this.translateService.instant(prefix + 'error-iban-size', errors['ibanSize']);
    }
    if (errors['ibanLength']) {
      return this.translateService.instant(prefix + 'error-iban-length', errors['ibanLength']);
    }
    if (errors['ibanChecksum']) {
      return this.translateService.instant(prefix + 'error-iban-checksum');
    }
    return '';
  }

  /** Campi non validi con il relativo messaggio, mostrati in un riepilogo dopo un tentativo di salvataggio. */
  get invalidFields(): { label: string; message: string }[] {
    const labels: { [field: string]: string } = {
      employer_type: 'field-employer-type',
      employer_name: 'field-employer-name',
      iban: 'field-iban',
      tax_code: 'field-tax-code',
      fiscal_regime: 'field-fiscal-regime',
    };

    return Object.keys(labels)
      .filter((field) => this.form.get(field)?.invalid)
      .map((field) => ({
        label: this.translateService.instant('components.clinical-onboarding.wizard.' + labels[field]),
        message: this.fieldError(field),
      }));
  }

  private applyServerErrors(error: any): void {
    const fieldErrors = error?.error?.errors;
    if (error?.status !== 422 || !fieldErrors) {
      return;
    }

    Object.keys(fieldErrors).forEach((field) => {
      const control = this.form.get(field);
      if (control) {
        control.setErrors({ ...control.errors, server: [].concat(fieldErrors[field]).join(' ') });
        control.markAsTouched();
      }
    });
  }

  private updateEmployerNameValidator(employerType: string): void {
    const employerNameControl = this.form.get('employer_name');
    if (employerType === EmployerType.PUBLIC) {
      employerNameControl.setValidators(Validators.required);
    } else {
      employerNameControl.clearValidators();
    }
    employerNameControl.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.updateEmployerNameValidator(this.form.get('employer_type').value);
    this.route.params.subscribe((params) => {
      this.candidateId = params.candidate_id;
      this.load();
    });
  }

  load() {
    this.loading = true;
    this.clinicalOnboardingService.getMyEngagement(
      this.candidateId,
      (data) => {
        this.loading = false;
        this.macroStatus = data.engagement.macro_status;
        this.paAuthorizationStatus = data.engagement.pa_authorization_status;
        this.manualOverride = data.engagement.manual_participation_override;
        this.canEditBankFiscalData = data.engagement.can_edit_bank_fiscal_data;
        this.documents = data.documents || [];
        this.surveyId = data.survey?.id ?? null;
        this.surveyIsDraft = data.survey?.is_draft ?? true;
        this.imported = data.imported ?? false;

        this.form.patchValue({
          title: data.engagement.title,
          employer_type: data.engagement.employer_type,
          employer_name: data.engagement.employer_name,
          fiscal_regime: data.engagement.fiscal_regime,
        });

        if (!this.canEditBankFiscalData) {
          this.form.disable();
        }
      },
      () => {
        // Feedback utente: un accesso non autorizzato (candidate_id di un altro
        // clinico, survey/candidato non trovato, feature non abilitata - il
        // backend risponde sempre con un errore in questi casi, mai un 200 vuoto)
        // non deve mostrare la pagina vuota/rotta: redirect immediato, senza mai
        // uscire dallo stato di caricamento (niente flash del guscio della card
        // prima del redirect). Stesso pattern gia' in uso in
        // survey-response-page.component.ts per lo stesso tipo di errore.
        this.router.navigate(['/']);
      },
    );
  }

  get isEmployerPublic() {
    return this.form.get('employer_type').value === EmployerType.PUBLIC;
  }

  save() {
    this.saveAttempted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const value = this.form.value;

    this.clinicalOnboardingService.updateMyEngagement(
      this.candidateId,
      {
        title: value.title,
        employer_type: value.employer_type,
        employer_name: value.employer_name,
        iban: value.iban ? normalizeIban(value.iban) : null,
        tax_code: value.tax_code || null,
        fiscal_regime: value.fiscal_regime || null,
      },
      () => {
        this.saving = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.wizard.saved'),
        );
        // Non richiediamo di ridigitare IBAN/codice fiscale gia' salvati: il form li
        // mostra sempre vuoti (il backend non li restituisce mai in chiaro), quindi qui
        // basta ricaricare lo stato per riflettere pa_authorization_status/has_iban.
        // Un cambio di datore di lavoro puo' anche far comparire/sparire documenti
        // condizionati (visible_only_if_employer_public), quindi ricarica anche quelli.
        this.load();
      },
      (error) => {
        this.saving = false;
        this.applyServerErrors(error);
      },
    );
  }

  /**
   * Presa visione (piano §3): apre il file in una nuova scheda tramite l'endpoint di
   * streaming, che registra la presa visione lato server al primo accesso - niente
   * bottone "segna come letto" separato. Ricarica lo stato subito dopo, cosi' il
   * badge passa a "Visto"/l'eventuale sblocco del pulsante "vai al survey" si
   * riflettono senza bisogno di un refresh manuale.
   */
  viewDocument(document: ClinicianCandidateDocument, which: 'source' | 'return' = 'source'): void {
    // which='return' (feedback utente: "non c'e' il tracciamento dell'evento di
    // scaricamento del file da parte del clinico") - il clinico puo' riaprire il
    // file che ha gia' restituito per controllare cosa ha effettivamente inviato;
    // tracciato lato backend (clinician_candidate_document.own_return_viewed) ma
    // non tocca viewed_at/status (quelli riguardano solo il file da vedere, non
    // quello restituito) quindi qui non serve ricaricare lo stato.
    if (which === 'return') {
      this.clinicalOnboardingService.openCandidateDocument(this.candidateId, document.id, 'return');
      return;
    }

    this.clinicalOnboardingService.openCandidateDocument(this.candidateId, document.id, 'source', () => this.load());
  }

  onDocumentFileSelected(document: ClinicianCandidateDocument, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.uploadingDocumentId = document.id;
    this.clinicalOnboardingService.uploadCandidateDocumentReturn(
      this.candidateId,
      document.id,
      file,
      () => {
        this.uploadingDocumentId = null;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.wizard.document-returned'),
        );
        this.load();
      },
      () => {
        this.uploadingDocumentId = null;
      },
    );

    input.value = '';
  }

  goToSurveyResponse() {
    this.router.navigate(['/surveys', this.surveyId, 'response']);
  }

  /** Idoneita' di merito (documenti/firma) - indipendente da import/pubblicazione survey. */
  get canSubmitResponse() {
    return (
      this.manualOverride ||
      [ClinicianMacroStatus.SIGNED, ClinicianMacroStatus.INVOICE_RECEIVED, ClinicianMacroStatus.PAID].includes(
        this.macroStatus as any,
      )
    );
  }

  /**
   * Idoneo per merito MA anche gia' importato in un vero team member su una survey
   * pubblicata: solo a queste condizioni /surveys/{id}/response accetta l'accesso
   * (userCanAnswer() lato backend richiede un team member reale per email) - un
   * candidato puo' completare l'intero pacchetto (piano §"registrare i futuri
   * medici prima che la survey sia partita") prima che l'importazione avvenga.
   */
  get canGoToSurveyResponse() {
    return this.canSubmitResponse && this.imported && !this.surveyIsDraft;
  }

  /** Idoneo per merito ma ancora in attesa di importazione nel team reale della survey. */
  get eligibleButNotImported() {
    return this.canSubmitResponse && !this.canGoToSurveyResponse;
  }
}
